"use client";

import { clsx } from "@frend-digital/ui";
import { useEmblaCarousel } from "@frend-digital/ui/embla";

import Chevron from "@/components/icons/Chevron";
import ChevronLeft from "@/components/icons/ChevronLeft";
import { useEmblaNavigation } from "@/lib/hooks/useEmblaNavigation/useEmblaNavigation";
import { EmblaCarouselType } from "embla-carousel";
import Link from "next/link";
import {
  type ComponentProps,
  type RefObject,
  useImperativeHandle,
} from "react";
import styles from "./Carousel.module.css";

export type LinkReturn = {
  href: string;
  rawHref: string;
  email?: string;
  target?: "_self" | "_blank";
  anchor?: string | unknown;
  type: "story" | "url" | "email";
  external?: boolean;
} | null;

export interface CarouselProps
  extends Omit<ComponentProps<"div">, "className" | "link" | "ref" | "title"> {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  ref?: RefObject<EmblaCarouselType | undefined>;
  showControls?: boolean;
  activeScroll?: boolean;
  title?: string;
  link?: LinkReturn;
  linkText?: string;
  wrapperClassName?: string;
  titleStyle?: string;
}

export const Carousel = ({
  children,
  className,
  containerClassName,
  ref: refProp,
  showControls,
  activeScroll,
  title,
  link,
  linkText,
  wrapperClassName,
  titleStyle,
  ...rest
}: CarouselProps) => {
  const [ref, emblaAPI] = useEmblaCarousel({
    loop: false,
    align: "start",
    dragFree: activeScroll,
  });

  useImperativeHandle(refProp, () => emblaAPI.current);
  const { nextDisabled, onNextClick, onPrevClick, prevDisabled } =
    useEmblaNavigation(emblaAPI);
  return (
    <div className={clsx(styles.root, wrapperClassName)} {...rest}>
      {(title || (linkText && link) || showControls) && (
        <div className={styles.header}>
          <div className={styles.headerInner}>
            {title && <h2 className={titleStyle}>{title}</h2>}
            {linkText && link && (
              <Link href={link?.href} className={styles.link}>
                {linkText}
                <span className={styles.chevron}>
                  <Chevron />
                </span>
              </Link>
            )}
          </div>
          {showControls && (
            <div className={styles.controls}>
              <div className={styles.buttons}>
                <button
                  type="button"
                  aria-label="Previous"
                  onClick={onPrevClick}
                  disabled={prevDisabled}
                >
                  <Chevron scale={1.3} />
                </button>
                <button
                  type="button"
                  aria-label="Next"
                  onClick={onNextClick}
                  disabled={nextDisabled}
                >
                  <ChevronLeft scale={1.3} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className={clsx(styles.sliderRoot, className)} ref={ref}>
        <div className={clsx(styles.container, containerClassName)}>
          {children}
        </div>
      </div>
    </div>
  );
};
